import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { isPrerendering } from '../prerenderData';
import { AWNING_ORDER } from '../data/homepageData';
import { AWNING_WARRANTY_YEARS, WARRANTY_COVER, EXPECTED_LIFE_YEARS } from '../config/warranty';
import { AWNING_FROM_PRICE, AWNING_FROM_LABEL } from '../config/startingPrice';
import { STOCK_COLOUR_NAME, LEAD_STOCK, MADE_TO_ORDER_BANDS } from '../config/leadTimes';
import './AwningsPage.css';
import { apiFetch } from '../services/api';
import { cardImage } from '../utils/productImages';

const AW01_VARIANTS = [
  { name: 'Classic Mid Grey', code: 'ASG 01', image: '/images/products/awnings/awning-mid-grey.webp', swatch: '#A9A6A1' },
  { name: 'Soft Off-White', code: 'ASG 02', image: '/images/products/awnings/awning-off-white.webp', swatch: '#E8E6E1' },
  { name: 'Warm Sand Beige', code: 'ASG 03', image: '/images/products/awnings/awning-warm-sand-beige.webp', swatch: '#CBB394' },
  { name: 'Deep Espresso Brown', code: 'ASG 04', image: '/images/products/awnings/awning-espresso-brown-v2.webp', swatch: '#3B2E2A' },
  { name: 'Monument Charcoal', code: 'ASG 05', image: '/images/products/awnings/awning-monument-charcoal-v2.webp', swatch: '#5A544C' },
];

// Static fallback used during prerender / API failure. Mirrors DB.
const AWNING_FALLBACK = [
  {
    _id: 'fallback-aw01',
    slug: 'aw01-full-box-awning',
    name: 'AW01 Full Cassette Awning',
    category: 'awning',
    sort_order: 1,
    price_from: 1000,
    short_description: 'Premium motorised retractable awning with a fully enclosed cassette - 5 curated fabric colours, integrated LED lighting, wind sensor, and electric or manual operation. Maximum single set 7 m × 3.5 m.',
    images: AW01_VARIANTS.map((v) => v.image),
    color_variants: AW01_VARIANTS,
  },
  {
    _id: 'fallback-b06',
    slug: 'b06-premium-open-arm-awning',
    name: 'B06 Premium Open-Arm Awning',
    category: 'awning',
    sort_order: 2,
    price_from: 1000,
    short_description: 'Premium open-arm retractable awning with integrated LED light strips in the arms, stainless steel chain link joints. Clean modern lines, no valance. Up to 6 m × 3 m.',
    images: ['/images/products/awnings/awning-b06-stock.webp'],
    color_variants: [],
  },
  {
    _id: 'fallback-b05',
    slug: 'b05-traditional-scalloped-awning',
    name: 'B05 Traditional Scalloped Awning',
    category: 'awning',
    sort_order: 3,
    price_from: 1000,
    short_description: 'Traditional scalloped retractable awning with a choice of white or frosted black finish. Stainless chain link arms, decorative scalloped valance. Up to 6 m × 3 m.',
    images: ['/images/products/awnings/awning-b05-stock.webp'],
    color_variants: [],
  },
  {
    _id: 'fallback-a09',
    slug: 'a09-long-projection-awning',
    name: 'A09 Long-Projection Awning',
    category: 'awning',
    sort_order: 4,
    price_from: 1000,
    short_description: 'Long-projection awning built for deep alfresco areas. Reinforced heavy-gauge 82 mm arms, double plate bracket. Up to 6 m × 4 m - the deepest projection.',
    images: ['/images/products/awnings/awning-a09-stock.webp'],
    color_variants: [],
  },
  {
    _id: 'fallback-k3',
    slug: 'k3-heritage-mediterranean-awning',
    name: 'K3 Heritage Mediterranean Awning',
    category: 'awning',
    sort_order: 5,
    price_from: 1000,
    short_description: 'Heritage Mediterranean-style awning with a traditional pointed-tab valance. Aluminium curved arms with thickened joint heads. Perfect for Federation, period weatherboard and Mediterranean villa homes.',
    images: ['/images/products/awnings/awning-k3-stock.webp'],
    color_variants: [],
  },
];

// Per-model marketing meta keyed by slug
const MODEL_META = {
  'aw01-full-box-awning': {
    badge: 'Most Popular',
    aesthetic: 'Modern, fully enclosed cassette',
    valance: 'None (cassette)',
    led: 'Integrated',
    maxSize: '7.0 × 3.5 m',
  },
  'b06-premium-open-arm-awning': {
    badge: 'New generation',
    aesthetic: 'Modern, no valance',
    valance: 'None - clean front bar',
    led: 'Integrated in arms',
    maxSize: '6.0 × 3 m',
  },
  'a09-long-projection-awning': {
    badge: 'Deepest projection',
    aesthetic: 'Heavy-gauge arms, traditional valance',
    valance: 'Straight with corner cutouts',
    led: 'Not available',
    maxSize: '6.0 × 4 m',
  },
  'b05-traditional-scalloped-awning': {
    badge: 'White or frosted black',
    aesthetic: 'Traditional, scalloped',
    valance: 'Scalloped',
    led: 'Not available',
    maxSize: '6.0 × 3 m',
  },
  'k3-heritage-mediterranean-awning': {
    badge: 'Heritage entry',
    aesthetic: 'Mediterranean, period homes',
    valance: 'Pointed / triangular tabs',
    led: 'Not available',
    maxSize: '6.0 × 3 m',
  },
};

// Order by the shared AWNING_ORDER (data/homepageData) so this page and the
// homepage showcase never drift. Unknown slugs sink last.
//
// Applied to the FALLBACK as well as the API response: the fallback is what the
// pre-rendered HTML renders, so sorting only the API path meant the static page
// (first paint, and what crawlers read) disagreed with the hydrated order.
function sortByAwningOrder(list) {
  return [...list].sort((a, b) => {
    const ia = AWNING_ORDER.indexOf(a.slug);
    const ib = AWNING_ORDER.indexOf(b.slug);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

function AwningsPage() {
  // Seeded from the build-time snapshot on the client too, so hydration
  // adopts the pre-rendered page rather than rebuilding it from empty.
  const [awnings, setAwnings] = useState(sortByAwningOrder(AWNING_FALLBACK));
  const [loading, setLoading] = useState(false);
  const [aw01Colour, setAw01Colour] = useState(0);

  useEffect(() => {
    if (isPrerendering) return;
    apiFetch('/api/products')
      .then((r) => r.json())
      .then((all) => {
        const filtered = sortByAwningOrder((all || []).filter((p) => p.category === 'awning'));
        setAwnings(filtered.length ? filtered : sortByAwningOrder(AWNING_FALLBACK));
        setLoading(false);
      })
      .catch(() => { setAwnings(sortByAwningOrder(AWNING_FALLBACK)); setLoading(false); });
  }, []);

  const aw01 = awnings.find((p) => p.slug === 'aw01-full-box-awning');
  const aw01Variants = (aw01 && aw01.color_variants && aw01.color_variants.length > 0)
    ? aw01.color_variants
    : AW01_VARIANTS;
  const heroImage = aw01Variants[aw01Colour]?.image || aw01Variants[0].image;

  return (
    <div className="awnings-page">
      <SEO
        title="Retractable Awnings Melbourne - Free Delivery"
        description={`Made-to-measure retractable awnings in Melbourne. Five models from full cassette to heritage Mediterranean, free delivery to most of Melbourne. ${AWNING_FROM_LABEL}.`}
        keywords="awnings Melbourne, retractable awning, motorised awning, full box awning, cassette awning, open arm awning, heritage awning, long-projection awning"
        canonical="/products/awnings"
        jsonLd={[
          SEO.serviceSchema,
          SEO.breadcrumbSchema([{ name: 'Products', url: '/products' }, { name: 'Awnings' }]),
          SEO.webPageSchema({
            type: 'CollectionPage',
            name: 'Retractable Awnings Melbourne',
            description: 'Five-model retractable awning range, made to measure with free delivery to most of Melbourne.',
            url: '/products/awnings',
          }),
        ]}
      />

      <Breadcrumbs items={[{ name: 'Products', url: '/products' }, { name: 'Awnings' }]} />

      {/* Hero */}
      <section className="aw-hero">
        <div className="aw-hero-image">
          <img
            src={heroImage}
            alt={`AW01 awning in ${aw01Variants[aw01Colour]?.name || 'standard'} fabric`}
            loading="eager"
            width="1024"
            height="1024"
            key={aw01Colour}
          />
        </div>
        <div className="aw-hero-content">
          <p className="aw-label">Awnings</p>
          <h1 className="aw-hero-title">
            Premium retractable awnings, delivered across Melbourne.
          </h1>
          <p className="aw-hero-subtitle">
            A curated five-model range - from the fully-enclosed cassette flagship to the heritage
            Mediterranean entry. All aluminium, all engineered for the Australian
            climate. {AWNING_FROM_LABEL}, made to measure, with free delivery to most of Melbourne.
          </p>
          <div className="aw-hero-actions">
            <Link to="/request-quote" className="asg-btn-primary">Request a Quote</Link>
            <Link to="/products/compare" className="asg-btn-secondary">Compare Models</Link>
          </div>
        </div>
      </section>

      {/* Why an awning */}
      <section className="aw-intro">
        <div className="aw-intro-inner">
          <p className="aw-section-label">Why an awning</p>
          <h2 className="aw-section-title">
            Instant shade. Hidden away when you don't need it.
          </h2>
          <p className="aw-section-body">
            Retractable awnings give you flexible sun and shade at the push of a button. When
            extended, the Sauleda acrylic fabric blocks 99% of UVA. When retracted, the system
            tucks back against the wall - so the look stays clean and the fabric stays protected.
            Every model is made to measure - the AW01 and B06 are motorised, the A09, B05 and K3
            are manual - with <Link to="/delivery">free delivery to most of Melbourne</Link>.
          </p>
        </div>
      </section>

      {/* Range grid */}
      <section className="aw-range">
        <div className="aw-range-inner">
          <p className="aw-section-label">The Range</p>
          <h2 className="aw-section-title">Five awnings, five distinct looks.</h2>
          <p className="aw-section-body" style={{ maxWidth: 720, margin: '0 auto 48px' }}>
            Each model in the range is designed for a different aesthetic and use case - from the
            fully-enclosed cassette flagship to the long-projection specialist and the
            heritage Mediterranean entry. Tap any model for full specifications.
          </p>
          {loading && <p style={{ textAlign: 'center', color: '#888' }}>Loading range…</p>}
          <div className="aw-range-grid">
            {awnings.map((p) => {
              const meta = MODEL_META[p.slug] || {};
              const img = cardImage(p) || '/images/products/awnings/awning-mid-grey.webp';
              return (
                <Link to={`/products/${p.slug}`} key={p._id || p.slug} className="aw-range-card">
                  <div className="aw-range-card-img">
                    <img src={img} alt={p.name} loading="lazy" width="800" height="800" />
                    {meta.badge && <span className="aw-range-badge">{meta.badge}</span>}
                  </div>
                  <div className="aw-range-card-body">
                    <h3 className="aw-range-card-name">{p.name}</h3>
                    <p className="aw-range-card-desc">{p.short_description}</p>
                    <ul className="aw-range-card-meta">
                      {meta.aesthetic && <li><strong>Aesthetic:</strong> {meta.aesthetic}</li>}
                      {meta.valance && <li><strong>Valance:</strong> {meta.valance}</li>}
                      {meta.led && <li><strong>LED:</strong> {meta.led}</li>}
                      {meta.maxSize && <li><strong>Max size:</strong> {meta.maxSize}</li>}
                    </ul>
                    <span className="aw-range-card-cta">View specs →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spanish Sauleda fabric collection */}
      {/* id="colours" is the target of the main nav's "Colours" item. */}
      <section className="aw-fabrics" id="colours">
        <div className="aw-fabrics-inner">
          <p className="aw-section-label">Fabric Collection</p>
          <h2 className="aw-section-title">Spanish Sauleda - 19 solids + 18 stripes.</h2>
          <p className="aw-section-body">
            Every awning in the range uses premium <strong>Spanish Sauleda</strong> fabric -
            dyed through the fibre, UV-stable, covered by our <strong>{AWNING_WARRANTY_YEARS}-year warranty</strong> and built to last {EXPECTED_LIFE_YEARS} years or more.
            Choose from 19 solid colours and 18 striped patterns, from contemporary charcoals
            and earth tones through to heritage burgundy stripes and bright accents. Every
            model can be specified in any of the colours below.
          </p>
          <div className="aw-fabrics-image">
            <img
              src="/images/products/awnings/awning-fabric-collection.webp"
              alt="ASG Shading Spanish Sauleda awning fabric collection - 19 solid colours plus 18 stripes including off-white, warm beige, mid grey, walnut brown, deep red, burgundy, indigo blue, charcoal grey, emerald green, teal green, and various heritage stripes"
              loading="lazy"
              width="1920"
              height="940"
            />
          </div>
          <p className="aw-fabrics-note">
            Tap any model below for full specs. Prefer to see the fabrics in person? Order a
            free sample pack and we'll post the swatches to you.
          </p>
        </div>
      </section>

      {/* AW01 colour story (only flagship has live variants today) */}
      {aw01 && (
        <section className="aw-colours">
          <div className="aw-colours-inner">
            <p className="aw-section-label">Default 5 Colours</p>
            <h2 className="aw-section-title">5 curated defaults for the flagship AW01.</h2>
            <p className="aw-section-body">
              These are the 5 default fabric colours for the flagship AW01 - chosen to suit the
              most common Australian home palettes. {STOCK_COLOUR_NAME} is held in stock and can
              often ship the {LEAD_STOCK}; the other defaults, and any colour from the full Spanish
              Sauleda range above, are made to order: {MADE_TO_ORDER_BANDS}.
            </p>
            <div className="aw-colour-grid">
              {aw01Variants.map((v, i) => (
                <button
                  key={v.code}
                  type="button"
                  className={`aw-colour-card ${i === aw01Colour ? 'aw-colour-card--active' : ''}`}
                  onClick={() => setAw01Colour(i)}
                >
                  <img src={v.image} alt={`AW01 awning in ${v.name}`} loading="lazy" width="800" height="800" />
                  <div className="aw-colour-card-info">
                    <span className="aw-colour-swatch-dot" style={{ background: v.swatch || '#ccc' }} />
                    <span className="aw-colour-card-name">{v.name}</span>
                    <span className="aw-colour-card-code">{v.code}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Range capability specs */}
      <section className="aw-specs">
        <div className="aw-specs-inner">
          <div className="aw-specs-text">
            <p className="aw-section-label">Range Capability</p>
            <h2 className="aw-section-title">Built to your exact dimensions.</h2>
            <p className="aw-section-body">
              Every awning is measured, built to your spec, and made for your space - we
              don't sell fixed-size kits. Choose the model that suits your aesthetic, then size
              it to your wall.
            </p>
            <ul className="aw-specs-list">
              <li><strong>Starts from:</strong> ${AWNING_FROM_PRICE} supplied</li>
              <li><strong>Maximum width:</strong> up to 7 m on the AW01, 6 m on the other models (single set)</li>
              <li><strong>Maximum projection:</strong> up to 4 m (A09 model)</li>
              <li><strong>Operation:</strong> motorised with wireless remote, or manual, depending on the model</li>
              <li><strong>Frame:</strong> aluminium alloy, UV-stable powder coat</li>
              <li><strong>Fabric:</strong> Sauleda acrylic, blocks 99% of UVA</li>
              <li><strong>Mounting:</strong> wall (most common) or ceiling</li>
              <li><strong>Delivery:</strong> free to local Melbournians</li>
              <li><strong>Supply:</strong> supply-only - arrange your own installer</li>
              <li><strong>Warranty:</strong> {AWNING_WARRANTY_YEARS} years on {WARRANTY_COVER.awning.covers}</li>
            </ul>
            <div className="aw-hero-actions" style={{ marginTop: 24 }}>
              <Link to="/products/compare" className="asg-btn-secondary">Compare All Models</Link>
            </div>
          </div>
          <div className="aw-specs-image">
            <img src="/images/products/awning-aw01-dimensions.webp" alt="Awning dimensions reference" loading="lazy" width="800" height="800" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="aw-cta">
        <div className="aw-cta-inner">
          <h2>Ready to add an awning to your space?</h2>
          <p>Send us a couple of photos and your measurements. We'll confirm the right model and colour options and prepare a detailed written quote - no site visit, no obligation.</p>
          <div className="aw-cta-actions">
            <Link to="/request-quote" className="asg-btn-primary">Request a Quote</Link>
            <Link to="/products/compare" className="asg-btn-secondary">Compare Models</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AwningsPage;
